import type { InternalAxiosRequestConfig } from 'axios'
import { SM2CryptoUtils } from './SM2CryptoUtils'
import { SM3Utils } from './SM3Utils'
import { SM4CbcUtils } from './SM4CbcUtils'
import { bytesToHex, generateRandomString, getGlobalWindow, getPublicKey, objectToRawQueryString, type SecurityPublicKeyResponse } from './util'

type RequestConfigWithPublicKey = InternalAxiosRequestConfig & {
  _publicKeyRes?: SecurityPublicKeyResponse
}
import { getLocalSignSerialNo, signObj } from '..'

// 不需要加密处理的接口列表 （模糊匹配）
export const ExcludeUrls = [
  '/mchgateway/logon',
  'agentmerchant',
  'sassbff',
]

/**
 * 生成请求体签名拦截器
 * 用于对非GET请求的数据进行加密和签名处理
 */
export function generateBodySignInterceptor() {
  return async (config: InternalAxiosRequestConfig) => {
    const globalWindow = getGlobalWindow()

    // 调试信息输出
    // 使用不同颜色输出调试信息
    if ((import.meta as any).env.MODE !== 'production') {
      // eslint-disable-next-line no-console
      console.log(`%c[请求调试] URL: ${config.url}`, 'color: #1E90FF; font-weight: bold;')
      // eslint-disable-next-line no-console
      console.log(`%c[请求调试] 方法: ${config.method}`, 'color: #32CD32; font-weight: bold;')
      // eslint-disable-next-line no-console
      console.log('%c[请求调试] 数据:', 'color: #FFA500; font-weight: bold;', config.data)
    }

    // 检查当前请求是否在排除列表中
    if (ExcludeUrls.some(url => config.url?.includes(url)))
      return config

    const requestConfig = config as RequestConfigWithPublicKey
    const publicKeyRes = requestConfig._publicKeyRes ?? await getPublicKey().catch(() => {
      throw new Error('无法获取公钥，请求终止')
    })
    requestConfig._publicKeyRes = publicKeyRes

    // 只处理非GET请求且请求体不是FormData的情况
    if (config?.method?.toLowerCase() !== 'get' && !(config.data instanceof FormData)) {
      // 生成16位随机字符串作为nonce
      const nonce = generateRandomString(16)
      // 获取当前时间戳
      const timestamp = Date.now()

      // 生成SM4加密的密钥(16位随机字符串)
      const sm4Key = generateRandomString(16)
      // 将SM4密钥转换为十六进制格式
      const hexSm4Key = bytesToHex(new TextEncoder().encode(sm4Key))

      // 使用SM2算法和公钥对SM4密钥进行加密
      const enKey = SM2CryptoUtils.encrypt(new TextEncoder().encode(sm4Key), publicKeyRes.publicKeyRaw)

      // 生成初始化向量(IV)，用于SM4-CBC模式加密
      const iv = generateRandomString(16)
      // 将IV转换为十六进制格式
      const hexIv = bytesToHex(new TextEncoder().encode(iv))

      // 使用SM4-CBC模式加密请求数据，输出为Base64格式
      const enData = SM4CbcUtils.encryptToBase64(
        new TextEncoder().encode(JSON.stringify(config.data || {})),
        hexSm4Key,
        hexIv,
      )

      // 构建签名所需的参数映射
      const signatureMap = new Map<string, string>()
      signatureMap.set('enKey', enKey)
      signatureMap.set('enData', enData)
      signatureMap.set('nonce', nonce)
      signatureMap.set('timestamp', timestamp.toString())
      signatureMap.set('iv', iv)

      // 获取签名内容并使用SM3算法计算HMAC值
      const signSourceContent = SM3Utils.getSignatureContent(signatureMap)
      const sign = SM3Utils.calculateHmacSm3(signSourceContent, hexSm4Key)

      let signature = ''
      let serialNo: string | false = false

      const signType = globalWindow?.sessionStorage?.signType

      if (signType === 'SM2') {
      // 需要确保缓存中存在 operatorId， 因为 signObj.initContr 需要
        const operatorId = globalWindow?.sessionStorage?.operatorId
        serialNo = await getLocalSignSerialNo(operatorId)

        if (serialNo) {
          const strSignedSource = objectToRawQueryString({
            enData,
            enKey,
            iv,
            nonce,
            serialNo,
            sign,
            timestamp,
          })

          const getProviderRes = await signObj.getProviderAsync('coreCrypto', 0)
          const strSigned = await new Promise((resolve) => {
            if (!getProviderRes)
              resolve(false)
            signObj.initContr(operatorId)
            signObj.xtSignYB('', strSignedSource, (res) => {
              resolve(res)
            }, () => {
              resolve(false)
            })
          })

          signature = strSigned as string

          if ((import.meta as any).env.MODE !== 'production') {
          // eslint-disable-next-line no-console
            console.log('签名原文', strSignedSource)
            // eslint-disable-next-line no-console
            console.log('签名后', strSigned)
          }
        }
      }

      // 替换原始请求数据为加密后的数据和相关参数
      config.data = {
        enKey, // SM2加密后的SM4密钥
        enData, // SM4加密后的请求数据
        nonce, // 随机字符串，防重放
        timestamp, // 时间戳，防重放
        iv, // 初始化向量
        sign, // 签名值，用于验证数据完整性

        // 如果序列号存在，添加序列号和签名字段
        ...((serialNo && signature) && {
          serialNo,
          signature,
        }),
      }
    }

    return config
  }
}
